import { useEffect, useRef, useState } from 'react'

import type { IconProp } from '../icon'
import type { CopyToClipboardProps } from './copyToClipboard.types'

import Button from '../button'
import { vui } from '../core'
import { Tooltip } from '../tooltip'
import { cs } from '../utils'
import { useCopyToClipboard } from './useCopyToClipboard'

const defaultIcon: IconProp = 'uiCopy'
const checkedIcon: IconProp = 'uiCheck'

const getCopyValue = (props: CopyToClipboardProps): string => {
  if (props.label !== undefined) return props.label
  return props.copyText
}

/**
 * A helper button component.
 *
 * Copies the provided text to clipboard.
 *
 * Copying is available only for a secure environment (https://), otherwise the button is disabled.
 *
 */
export const CopyToClipboard = vui<'button', CopyToClipboardProps>((props, ref) => {
  const value = getCopyValue(props)
  const {
    disabled,
    copyText,
    label,
    className,
    icon = defaultIcon,
    size = 'sm',
    variant = 'tertiary',
    intent = 'brand',
    showTooltip = true,
    ...rest
  } = props
  const [state, setState] = useState<'idle' | 'copied'>('idle')
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      if (resetTimerRef.current !== undefined) clearTimeout(resetTimerRef.current)
    },
    [],
  )

  const texts: Record<'idle' | 'copied', { label: string; icon: IconProp }> = {
    idle: {
      label: `Copy ${value}`,
      icon: icon,
    },
    copied: {
      label: `Copied ${value}`,
      icon: checkedIcon,
    },
  }

  const { copy, isCopyDisabled } = useCopyToClipboard(value, { showToast: false })

  const button = (
    <Button
      aria-label={texts[state].label}
      className={cs('vui-copy-to-clipboard', className)}
      disabled={disabled || isCopyDisabled}
      icon={texts[state].icon}
      intent={intent}
      onClick={() => {
        copy()
        setState('copied')
        if (resetTimerRef.current !== undefined) clearTimeout(resetTimerRef.current)
        resetTimerRef.current = setTimeout(() => {
          setState('idle')
          resetTimerRef.current = undefined
        }, 1_000)
      }}
      ref={ref}
      size={size}
      variant={variant}
      {...rest}
    />
  )

  return showTooltip ? <Tooltip text={texts[state].label}>{button}</Tooltip> : button
})

CopyToClipboard.displayName = 'CopyToClipboard'
export default CopyToClipboard
